<script lang="ts" setup>
import type { TripRouteFreight } from "@/types/trip";
import { computed } from "vue";
import { useFreights } from "@/composables";
import type { FreightChange } from "@/api/trips/dto/trip.dto";

const { hasFreightChanged } = useFreights();

const props = withDefaults(
  defineProps<{
    freights: TripRouteFreight[];
    freightsFromApi: TripRouteFreight[];
    label?: string;
  }>(),
  {
    label: "Route stop changes",
  },
);

function hasApiRecordedChange(freight: TripRouteFreight): boolean {
  return (
    freight.oldPlannedQuantity != null ||
    freight.oldPlannedWeight != null ||
    freight.oldType != null ||
    freight.oldStackable != null
  );
}

function buildChange(freight: TripRouteFreight, previous: TripRouteFreight | undefined): FreightChange | null {
  // First compare the initial (from API) freight against the current one.
  if (previous && hasFreightChanged(previous, freight)) {
    const change: FreightChange = {
      freight,
      previousQuantity: previous.plannedQuantity,
      previousWeight: previous.plannedWeight,
      previousType: previous.type,
      previousStackable: previous.stackable,
      quantityChanged: Number(freight.plannedQuantity) !== Number(previous.plannedQuantity),
      weightChanged: Number(freight.plannedWeight) !== Number(previous.plannedWeight),
      typeChanged: freight.type !== previous.type,
      stackableChanged: Boolean(freight.stackable) !== Boolean(previous.stackable),
    };

    return hasChanges(change) ? change : null;
  }

  // Initial and current are equal — fall back to the API-recorded old_planned values.
  if (hasApiRecordedChange(freight)) {
    return {
      freight,
      previousQuantity: freight.oldPlannedQuantity ?? freight.plannedQuantity,
      previousWeight: freight.oldPlannedWeight ?? freight.plannedWeight,
      previousType: freight.oldType ?? freight.type,
      previousStackable: freight.oldStackable ?? freight.stackable,
      quantityChanged: freight.oldPlannedQuantity != null,
      weightChanged: freight.oldPlannedWeight != null,
      typeChanged: freight.oldType != null,
      stackableChanged: freight.oldStackable != null,
    };
  }

  return null;
}

function hasChanges(change: FreightChange): boolean {
  return change.quantityChanged || change.weightChanged || change.typeChanged || change.stackableChanged;
}

const changedFreights = computed(() => {
  const freightFromApiById = new Map(props.freightsFromApi.map((freight) => [freight.id, freight]));

  return props.freights.reduce<FreightChange[]>((acc, freight) => {
    const change = buildChange(freight, freightFromApiById.get(freight.id));

    if (change) {
      acc.push(change);
    }

    return acc;
  }, []);
});
</script>

<template>
  <v-menu v-if="changedFreights.length > 0" location="top start" offset="4" width="500">
    <template #activator="{ props: menuProps }">
      <v-icon
        class="ml-2"
        color="warning"
        data-qa="alert-icon"
        icon="mdi-alert-circle-outline"
        size="20"
        v-bind="menuProps"
      ></v-icon>
    </template>
    <v-card>
      <v-card-text>
        <div class="font-size-16 text-grey-darken-3 font-weight-500 mb-2">{{ label }}</div>
        <div class="bg-grey-lighten-4 pa-3 my-2">
          <div class="text-grey-darken-2 font-weight-500 mb-3">Freight changes</div>
          <v-row class="pb-0">
            <v-col>
              <div class="text-grey-darken-2">Previous:</div>
            </v-col>
            <v-col>
              <div class="text-grey-darken-2">Updated:</div>
            </v-col>
          </v-row>
          <v-row
            v-for="{
              freight,
              previousQuantity,
              previousWeight,
              previousType,
              previousStackable,
              quantityChanged,
              weightChanged,
              typeChanged,
              stackableChanged,
            } in changedFreights"
            :key="freight.id"
            class="mt-0"
          >
            <v-col>
              <div class="text-grey-darken-2 font-weight-500" data-qa="modal-previous-freight-data">
                {{ freight.name }}: {{ previousQuantity }} {{ previousType }}, {{ previousWeight }} lbs,
                {{ previousStackable ? "Stackable" : "Not stackable" }}
              </div>
            </v-col>
            <v-col>
              <div class="text-grey-darken-2 font-weight-500" data-qa="modal-updated-freight-data">
                {{ freight.name }}:
                <span :class="{ 'text-warning': quantityChanged }">{{ freight.plannedQuantity }}</span>
                <span :class="{ 'text-warning': typeChanged }">{{ freight.type }}</span
                >, <span :class="{ 'text-warning': weightChanged }">{{ freight.plannedWeight }}</span> lbs,
                <span :class="{ 'text-warning': stackableChanged }">
                  {{ freight.stackable ? "Stackable" : "Not stackable" }}
                </span>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped></style>
